import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  InsertUprightTypeBody,
  UpdateUprightTypeNewImageBody,
  UpdateUprightTypeRemoveImageBody,
  UprightType,
} from "./types/uprightTypes";

const adminUprightTypesUrl = "/admin/settings/border/upright_types";

export const useUprightTypesApi = () => {
  const api = useApi();

  const getAdminUprightType = useCallback(
    (uprightTypeId: string, signal: AbortSignal) => {
      const url = `${adminUprightTypesUrl}/${uprightTypeId}`;
      return useSWRMutation<UprightType, CustomError, string>(url, (url) =>
        api.get<UprightType>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminUprightTypes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(adminUprightTypesUrl, query);
      return useSWRImmutable<Paginated<UprightType>, CustomError, string>(url, (url) =>
        api.get<Paginated<UprightType>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminUprightTypes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(adminUprightTypesUrl, query);
      return api.get<Paginated<UprightType>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminUprightType = useCallback(
    async (body: InsertUprightTypeBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertUprightTypeBody>(adminUprightTypesUrl, body);
      mutateCache([adminUprightTypesUrl]);
      return id;
    },
    [api],
  );

  const updateAdminUprightType = useCallback(
    async (
      uprightTypeId: string,
      body: UpdateUprightTypeRemoveImageBody | UpdateUprightTypeNewImageBody,
    ): Promise<void> => {
      const url = `${adminUprightTypesUrl}/${uprightTypeId}`;
      await api.put<UpdateUprightTypeRemoveImageBody | UpdateUprightTypeNewImageBody>(url, body);
      mutateCache([
        adminUprightTypesUrl,
        "/admin/settings/border/uprights",
        "/admin/settings/border/upright_caps",
        "/admin/settings/border/upright_mappings",
        "/admin/settings/border/upright_span_mappings",
      ]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (uprightTypeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `${adminUprightTypesUrl}/${uprightTypeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminUprightTypeVisibility = useCallback(
    async (uprightTypeId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `${adminUprightTypesUrl}/${uprightTypeId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility), mutateCache([adminUprightTypesUrl]));
    },
    [api],
  );

  const updateAdminUprightTypeOrder = useCallback(
    async (uprightTypeId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `${adminUprightTypesUrl}/${uprightTypeId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order), mutateCache([adminUprightTypesUrl]));
    },
    [api],
  );

  const deleteAdminUprightType = useCallback(
    async (uprightTypeId: string): Promise<void> => {
      const url = `${adminUprightTypesUrl}/${uprightTypeId}`;
      await api.del(url);
      mutateCache([adminUprightTypesUrl]);
    },
    [api],
  );

  return {
    getAdminUprightType,
    useAdminUprightTypes,
    getAdminUprightTypes,
    insertAdminUprightType,
    updateAdminUprightType,
    updateAdminUprightTypeVisibility,
    updateAdminUprightTypeOrder,
    deleteAdminUprightType,
    generateUploadImageUrl,
  };
};
